<script setup lang="ts">
import RcShipmentSelect from '@/components/rc/common/RcShipmentSelect.vue'
import WOMinersSelect from '@/components/rc/common/RcWOMinersSelect.vue'
import WorkOrderSelect from '@/components/rc/common/RcWorkOrderSelect.vue'
import { useContainers } from '@/composables/rc/containers'
import { ContainerStatus, ContainerTypeSelectOptions } from '@/types/rc/containers'
import type { IContainerForm } from '@/types/rc/forms'
import { ShipmentType } from '@/types/rc/shipments'
import type { FormInstance, FormRules } from 'element-plus'
import { computed, reactive, ref, watchEffect } from 'vue'
import { vMaska } from 'maska/vue'

// Interface
withDefaults(defineProps<{ isUpdate: boolean; lockWorkOrder: boolean }>(), { isUpdate: false, lockWorkOrder: false })
const containerForm = defineModel<Omit<IContainerForm, 'damages'>>({ required: true })

// Local state
const selectedShipment = ref<{ id: number }>()
const selectedStatus = ref<ContainerStatus>()
const selectMinersRef = ref<InstanceType<typeof WOMinersSelect>>()

const isInbound = computed(() => containerForm.value.kind == ShipmentType.Inbound)
const isBuilding = computed(() => containerForm.value.status == ContainerStatus.Building)

// Since the form in the parent is loaded upon query resolution, we need to watch it to sync with local state
watchEffect(() => {
  selectedShipment.value = containerForm.value.shipment ? { id: containerForm.value.shipment } : undefined
  selectedStatus.value = containerForm.value.status as ContainerStatus
})

const formRef = ref<FormInstance>()

// Exposed to parent
const validate = () => (formRef.value ? formRef.value.validate() : Promise.resolve(true))

// Exposed to parent
const reset = () => {
  formRef.value?.resetFields()
  selectMinersRef.value?.reset()
}

// Expose and provide an interface for exposed values
const exposed = { validate, reset }
defineExpose(exposed)
export type IContainerInputFormExposed = typeof exposed

const formRules = reactive<FormRules<IContainerForm>>({
  work_order: [{ required: true, message: 'Work order is required.', trigger: ['change', 'blur'] }],
  container_number: [
    { required: true, message: 'Container Number is required.', trigger: 'blur' },
    {
      type: 'pattern',
      pattern: /^[a-zA-Z0-9\-_()]+$/,
      message: 'Container number is not valid',
      trigger: ['blur', 'change'],
    },
  ],
})

// Server data
const { containerStatusesOptionsQuery, updateContainerStatusMutation } = useContainers()
const { data: containerStatuses } = containerStatusesOptionsQuery

const { mutate: updateContainerStatus } = updateContainerStatusMutation

const onStatusUpdate = (status: string) => {
  if (!selectedStatus.value) return
  updateContainerStatus(
    { id: containerForm.value.id!, status },
    {
      onSuccess: () => {
        containerForm.value.status = selectedStatus.value!
      },
      onError: () => {
        selectedStatus.value = containerForm.value.status
      },
    }
  )
}

const setContainerFormShipment = (shipment: { id: number } | undefined) => {
  containerForm.value.shipment = shipment?.id
}
</script>

<template>
  <el-form
    ref="formRef"
    class="form"
    :model="containerForm"
    :rules="formRules"
    label-position="top"
    label-width="160px"
    hide-required-asterisk
  >
    <!-- Work order -->
    <el-form-item label="Work Order Number" prop="work_order">
      <WorkOrderSelect
        v-model="containerForm.work_order"
        :disabled="isUpdate || !isBuilding || lockWorkOrder"
        placeholder="Search for a work order"
        @update:model-value="containerForm.shipment = undefined"
      />
    </el-form-item>
    <!-- Shipment -->
    <el-form-item label="Shipment" prop="shipment">
      <RcShipmentSelect
        v-model="selectedShipment"
        :type="containerForm.kind"
        :customer="containerForm.customer?.id"
        placeholder="Search for a shipment"
        @update:model-value="setContainerFormShipment"
      />
    </el-form-item>
    <!-- Status -->
    <el-form-item v-if="!isInbound && isUpdate" label="Status" prop="status">
      <el-select v-model="selectedStatus" @change="onStatusUpdate">
        <el-option
          v-for="status in containerStatuses"
          :key="status.value"
          :label="status.label"
          :value="status.value"
        />
      </el-select>
    </el-form-item>
    <!-- Container -->
    <el-form-item label="Container" prop="container_number">
      <el-col :span="17">
        <el-input v-model="containerForm.container_number" placeholder="Fill in container number" />
      </el-col>
      <el-col :span="1" />
      <el-col :span="6">
        <el-select v-model="containerForm.container_type" filterable default-first-option>
          <el-option
            v-for="option in ContainerTypeSelectOptions"
            :key="option.key"
            :label="option.value"
            :value="option.value"
          />
        </el-select>
      </el-col>
    </el-form-item>

    <div class="flex gap-7">
      <el-form-item label="Length (in)" prop="length">
        <el-input
          v-model="containerForm.length"
          v-maska
          data-maska="9999.9"
          data-maska-tokens="9:\d:optional"
          placeholder="0.0"
        />
      </el-form-item>
      <el-form-item label="Width (in)" prop="width">
        <el-input
          v-model="containerForm.width"
          v-maska
          data-maska="9999.9"
          data-maska-tokens="9:\d:optional"
          placeholder="0.0"
        />
      </el-form-item>
      <el-form-item label="Height (in)" prop="height">
        <el-input
          v-model="containerForm.height"
          v-maska
          data-maska="9999.9"
          data-maska-tokens="9:\d:optional"
          placeholder="0.0"
        />
      </el-form-item>
      <el-form-item label="Weight (lb)" prop="weight">
        <el-input
          v-model="containerForm.weight"
          v-maska
          data-maska="9999.9"
          data-maska-tokens="9:\d:optional"
          placeholder="0.0"
        />
      </el-form-item>
    </div>
    <!-- Miners -->
    <el-row>
      <el-col :span="17">
        <el-form-item label="Miners" prop="miner_models">
          <el-tooltip :disabled="isBuilding" content="Cannot modify miners unless status is 'Building'">
            <WOMinersSelect
              ref="selectMinersRef"
              v-model="containerForm.miners"
              :work-order-number="containerForm.work_order?.number"
              :disabled="!isBuilding"
            />
          </el-tooltip>
        </el-form-item>
      </el-col>
      <el-col :span="1" />
      <el-col :span="6">
        <el-form-item label="Expected miners" prop="miners_expected">
          <el-input-number v-model="containerForm.miners_expected" :min="1" placeholder="0" controls-position="right" />
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>
